import { AxisPointerComponentOption, DataZoomComponentOption, EChartsOption, LegendComponentOption, TitleComponentOption, TooltipComponentOption, XAXisComponentOption, YAXisComponentOption } from "echarts";
import { XorY } from "../type/XorY";
import { EchartsGraphConfig } from "../type/EchartGraphConfig";
import { StyleParam } from "../type/StyleParam";
import { StyleTarget } from "../type/StyleTarget";
import { Orientation } from "../type/Orientation";
import { StyleSize } from "../type/StyleSize";
import { Position } from "../type/Position";
import { Series } from "../type/Series";
import { Pointer } from "../type/Pointer";
import { Trigger } from "../type/Trigger";
import { Axis } from "../type/Axis";

export class EchartsGraph {

    private config: EchartsGraphConfig;

    /** Tooltip trigger & pointer */
    protected trigger: Trigger = 'axis';
    private pointer: Pointer = 'cross';
    
    /** Counter */
    private xAxisCount: number = 0;
    private yAxisCount: number = 0;
    private titleCount: number = 0;

    /** Colors RegExp */
    private hexaRegex: RegExp = new RegExp(/^#[0-9A-F]{6}$/i);
    private rgbRegex: RegExp = new RegExp(/^rgb\((( *0*([1]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5]) *),){2}( *0*([1]?[0-9]?[0-9]|2[0-4][0-9]|25[0-5]) *)\)$/);

    /** Options Skeleton */
    protected options: EChartsOption = {
        xAxis: [],
        yAxis: [],
        title: [],
        series: []
    }

    constructor(config: EchartsGraphConfig){
        this.config = config;
        this.options.yAxis = this.addYAxis(config.unit);
        if (this.config.xAxis) this.options.xAxis = this.addXaxis(config.xAxis);
        if (config.title) this.options.title = this.addTitles(config.title);
        if (config.legend === undefined || config.legend === true) this.options.legend = this.setLegend();
        if (config.tooltip === undefined || config.tooltip === true) this.options.tooltip = this.setTooltip();
        if (config.zoom === undefined || config.zoom === true) this.options.dataZoom = this.setDataZoom(this.xAxisCount, this.yAxisCount);
    }

    public setXaxisLabels(labels: any[]): void{
        for (let i = 0; i < labels.length; i++){
            this.options.xAxis[i].data = labels[i];
        }
    }

    public setSimpleGraphSeries(series: Series[]): void{
        this.setSeries(series);
    }

    protected setSeries(series: any[]): void{
        this.options.series = series;
    }

    public getOptions(): EChartsOption{
        return this.options;
    }

    /*********************************************************************************/
    /*********************************************************************************/
    /******************************OPTIONS BUILDERS***********************************/
    /*********************************************************************************/
    /*********************************************************************************/

    protected setLegend(): LegendComponentOption{
        return {
            orient: 'horizontal',
            formatter: function (name) {
                return name.toUpperCase();
            }
        }
    }

    protected setTooltip(): TooltipComponentOption{
        return {
            trigger: this.trigger,
            formatter: (params: any) => {
                return this.tooltipFormatter(params);
            },
            axisPointer: {
              animation: false,
              type: this.pointer
            },
            extraCssText: 'text-transform: uppercase;'
        }
    }

    private tooltipFormatter(params: any): string{
        if (this.trigger === 'item') return this.itemFormatter(params);
        if (this.trigger === 'axis') return this.axisFormatter(params);
    }

    private itemFormatter(params: any, name: any | null = null): string{
        const serie = this.options.series[params.seriesIndex]
        const yAxis = serie?.yAxisIndex ? serie.yAxisIndex : 0;
        const unit = this.config.unit[yAxis] ? this.config.unit[yAxis] : '';
        const n = name ? name.toUpperCase() : params.name.toUpperCase();
        return `<div>${params.marker} ${n} : ${params.value} ${unit.toUpperCase()}</div>`;
    }

    private axisFormatter(params: any[]): string{
        const formatter: string[] = [];
        params.forEach(p => {
            formatter.push(this.itemFormatter(p, p.seriesName));
        });
        return formatter.join('');
    }

    private setDataZoom(xAxisCount: number, yAxisCount: number): DataZoomComponentOption[]{
        const dataZoom = [];
        for (let i = 0; i < xAxisCount; i++) {
            dataZoom.push({xAxisIndex: i})
        }
        for (let i = 0; i < yAxisCount; i++) {
            dataZoom.push({yAxisIndex: i})
        }
        return dataZoom;
    }

    private addXaxis(type: Axis[]): XAXisComponentOption[]{
        const xAxis = [];
        type.forEach(t => {
            xAxis.push({
                type: t,
                data: [],
                axisLabel: {
                  rotate: 0
                }
            });
            this.xAxisCount++;
        });
        return xAxis;
    }

    private addYAxis(unit?: string[]): YAXisComponentOption[]{
        const yAxis = [];
        const units = unit && unit.length > 0 ? unit : [''];
        units.forEach(u => {
            this.yAxisCount++;
            yAxis.push({
                type: 'value',
                axisLabel: {
                  formatter: `{value} ${u}`
                }
            });
        })
        this.options.yAxis = yAxis;
        return yAxis;
    }

    private addTitles(title: string[]): TitleComponentOption[]{
        const titles = [];
        title.forEach(t => {
            this.titleCount++;
            titles.push({
                text: t
            })
        })
        return titles;
    }

    private setSharedPointer(): AxisPointerComponentOption{
        return {
          link: [
            {
              xAxisIndex: 'all',
            }
          ]
        }
      }

    /*********************************************************************************/
    /*********************************************************************************/
    /********************************POSITIONNING*************************************/
    /*********************************************************************************/
    /*********************************************************************************/

    /**
     * Use to position of some of the charts elements
     * @param target the element to be positionned
     * @param top position from the charts top
     * @param left position from the charts left
     * @param topPosCat category of positionning (by pixel, percentage or centered)
     * @param leftPosCat category of positionning (by pixel, percentage or centered)
     */
    private setPosition(target: any, position: Position){
        if (position.top) target.top = this.setCardinality(position.top);
        if (position.left) target.left = this.setCardinality(position.left);
        if (position.bottom) target.bottom = this.setCardinality(position.bottom);
        if (position.right) target.right = this.setCardinality(position.right);
    }

    protected setCardinality(cardinality: StyleSize){
        if (cardinality === 'center') return 'center';
        return cardinality.type ? cardinality.value + cardinality.type : cardinality.value;
    };

    public setLegendPosition(position: Position):void{
        if (this.options.legend) this.setPosition(this.options.legend, position);
    }
    
    public setZoomPosition(zoomIndex: number, position: Position):void{
        if (this.options.dataZoom && this.options.dataZoom[zoomIndex]) this.setPosition(this.options.dataZoom[zoomIndex], position);
    }

    public setTitlePosition(titleIndex: number, position: Position):void{
        if (this.options.title && this.options.title[titleIndex]) this.setPosition(this.options.title[titleIndex], position);
    }

    public rotateXAxis(axisIndex: number, rotate: number):void{
        if (this.options.xAxis && this.options.xAxis[axisIndex]) this.options.xAxis[axisIndex].axisLabel.rotate = rotate;
    }

    public changeTooltip(pointer: Pointer | null, trigger: Trigger | null): void{
        this.trigger = trigger;
        const tooltip = {
            trigger: trigger? trigger : this.trigger,
            formatter: (params: any) => {
                return this.tooltipFormatter(params);
            },
            axisPointer: {
              animation: false,
              type: pointer ? pointer : this.pointer
            },
            extraCssText: 'text-transform: uppercase;'
        }
        if (this.options.tooltip) this.options.tooltip = tooltip;
    }

    public changeLegendOrientation(orient: Orientation){
        if (this.options.legend) {
            this.setOrientation(this.options.legend, orient)
        }
    }

    private setOrientation(target: any, orient: Orientation){
        target.orient = orient;
    }

    /*********************************************************************************/
    /*********************************************************************************/
    /**********************************STYLING****************************************/
    /*********************************************************************************/
    /*********************************************************************************/

    protected isValidColor(color: string): boolean{
        const isValid = (this.hexaRegex.test(color) || this.rgbRegex.test(color));
        if (!isValid) console.warn('Echarts : invalid color');
        return isValid;
    }

    //series
    public setSerieColor(index: number, color: string, area: boolean = false): void {
        if (!this.isValidColor(color)) return;
        if (this.options.series && this.options.series[index] && this.options.series[index].type) {
            const type = this.options.series[index].type;            
            const itemStyle = {color: color};
            this.options.series[index].itemStyle = itemStyle;
            if (type === 'line') {
                const lineStyle = {color: color};
                this.options.series[index].lineStyle = lineStyle;
                if (area) {
                    const areaStyle = {color: color};
                    this.options.series[index].areaStyle = areaStyle;
                }
            }
            return;
        }
    }

    //shared
    protected setParam(target: any, count: number | null, index: number | null, value: number | string, param: StyleParam): void{
        let targetParam: StyleTarget = 'axisLabel';
        if (target == this.options.title) targetParam = 'textStyle';
        try {
            if (count === null) {
                this.setParamValue(target, null, targetParam, param, value);
                return;
            }
            if (count !== null && index === null) {
                if(target) {
                    for (let i = 0; i < count; i++) {
                        this.setParamValue(target, i, targetParam, param, value);
                    }
                }
                return
            }
            if (count !== null && index !== null && index < count) {
                this.setParamValue(target, index, targetParam, param, value);
                return;
            }
        } catch(e) {
            console.warn(e);
        }
    }

    protected setParamValue(target: any, index: number | null, targetParam: StyleTarget, param: StyleParam, value: number | string): void{
        if (target === this.options.title) this.initTitleTextStyle(index);
        if (target == this.options.xAxis || target == this.options.yAxis) if (param === 'color' && typeof value === 'string') this.setAxisLineColor(target[index], value);
        if (index === null) {
            target[targetParam][param] = value;
            return;
        }
        target[index][targetParam][param] = value;
    }

    protected initTitleTextStyle(i: number): void{
        if (this.options.title && this.options.title[i] && !this.options.title[i].textStyle) {
            this.options.title[i].textStyle = {};
        }
    }

    private setAxisLineColor(axis: XAXisComponentOption | YAXisComponentOption, color: string): void{
        if (!this.isValidColor(color)) return;
        if (axis && !axis.axisLine) axis.axisLine = {};
        if (axis && axis.axisLine && !axis.axisLine.lineStyle) axis.axisLine.lineStyle = {}
        axis.axisLine.lineStyle.color = color;
    }

    //title
    private setTitleParam(index: number | null, value: number | string, param: StyleParam): void {
        const title = this.options.title;
        const count: number = this.titleCount;
        this.setParam(title, count, index, value, param);
    }

    public setTitleColor(index: number | null, color: string): void {
        if (!this.isValidColor(color)) return;
        this.setTitleParam(index, color, 'color');
    }

    public setTitleSize(index: number | null, size: number): void {
        this.setTitleParam(index, size, 'fontSize');
    }

    public setTitleFont(index: number | null, font: string): void {
        this.setTitleParam(index, font, 'fontFamily');
    }

    public setTitleStyle(index: number | null, color: string | null, size: number | null, font: string | null): void{
        if (color !== null) this.setTitleColor(index, color);
        if (size !== null) this.setTitleSize(index, size);
        if (font !== null) this.setTitleFont(index, font);
    }

    //axis
    private setAxisParam(XorY: XorY, index: number | null, value: number | string, param: StyleParam): void {
        const axis: XAXisComponentOption | YAXisComponentOption = this.options[XorY + 'Axis'];
        const count: number = this[XorY + 'AxisCount'];
        this.setParam(axis, count, index, value, param);
    }

    public setAxisColor(XorY: XorY, index: number | null, color: string): void {
        if (!this.isValidColor(color)) return;
        this.setAxisParam(XorY, index, color, 'color');
    }

    public setAxisSize(XorY: XorY, index: number | null, size: number): void {
        this.setAxisParam(XorY, index, size, 'fontSize');
    }

    public setAxisFont(XorY: XorY, index: number | null, font: string): void {
        this.setAxisParam(XorY, index, font, 'fontFamily');
    }

    public setAxisStyle(XorY: XorY, index: number | null, color: string | null, size: number | null, font: string | null): void{
        if (color !== null) this.setAxisColor(XorY, index, color);
        if (size !== null) this.setAxisSize(XorY, index, size);
        if (font !== null) this.setAxisFont(XorY, index, font);
    }

    public setAllAxisStyle(color: string | null, size: number | null, font: string | null): void{
        this.setAxisStyle('x', null, color, size, font);
        this.setAxisStyle('y', null, color, size, font);
    }

    public setYaxisLimits(index: number, min: number|null, max: number|null){
        if(index > this.yAxisCount - 1) return;
        const yAxis = this.options.yAxis[index];
        if(min) yAxis.min = min;
        if(max) yAxis.max = max;
    }

    public setContentSize(left: number, right: number) {
        this.options.grid = {};
        if (left !== null) {
            this.options.grid.left = left;
        }
        if (right !== null) {
            this.options.grid.right = right;
        }
    }
}
